import React, { useState, useEffect } from "react";
import { Layout, Form, Input, Select, Switch, Button, Space, Row, Col, message, Card, Typography, Divider, Table, Modal, Tag, Tooltip, Tabs, Alert, Popconfirm, Steps } from "antd";
import { SaveOutlined, PlusOutlined, UploadOutlined, DeleteOutlined, EditOutlined, MailOutlined, CopyOutlined, ImportOutlined, CloseOutlined } from "@ant-design/icons";
import { masterDataService } from "../../../../api/enhancedApi";
import { formatDateTime } from "../../../../utils/dateUtils";

const { TextArea } = Input;
const { Option } = Select;
const { Title, Text } = Typography;
const { Header, Content } = Layout;
const { confirm } = Modal;
const { TabPane } = Tabs;
const { Step } = Steps;

/**
 * 分组管理抽屉组件 - 整合分组信息编辑和邮箱地址管理
 * @param {Object} props
 * @param {string} props.mode - 模式：'create', 'edit', 'view'
 * @param {Object} props.group - 分组数据
 * @param {Function} props.onSuccess - 成功回调
 * @param {Function} props.onCancel - 取消回调
 */
const GroupManagementDrawer = ({ mode, group, onSuccess, onCancel }) => {
    const [form] = Form.useForm();
    const [emailForm] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [availableTags, setAvailableTags] = useState([]);
    const [emailAddresses, setEmailAddresses] = useState([]);
    const [emailLoading, setEmailLoading] = useState(false);
    const [selectedEmails, setSelectedEmails] = useState([]);

    // 邮箱地址编辑相关状态
    const [editingKey, setEditingKey] = useState("");
    const [addingNew, setAddingNew] = useState(false);
    const [activeTab, setActiveTab] = useState("list");

    // 批量导入相关状态
    const [batchEmails, setBatchEmails] = useState("");
    const [batchLoading, setBatchLoading] = useState(false);
    const [validationResults, setValidationResults] = useState(null);

    // 创建模式的状态
    const [currentStep, setCurrentStep] = useState(0);
    const [newGroup, setNewGroup] = useState(null);

    // 名称验证状态
    const [nameValidating, setNameValidating] = useState(false);

    // 初始化数据
    useEffect(() => {
        loadAvailableTags();
        if (group && mode !== "create") {
            loadEmailAddresses();
            // 设置表单初始值
            form.setFieldsValue({
                name: group.name,
                description: group.description,
                tags: group.tags || [],
                is_active: group.is_active !== false,
            });
        } else if (mode === "create") {
            form.setFieldsValue({
                is_active: true,
            });
        }
    }, [group, mode, form]);

    const loadAvailableTags = async () => {
        try {
            const response = await masterDataService.emailGroup.getAvailableTags();
            setAvailableTags(response.data?.tags || []);
        } catch (error) {
            console.error("加载标签失败:", error);
        }
    };

    const loadEmailAddresses = async () => {
        const targetGroup = newGroup || group;
        if (!targetGroup?.id) {
            setEmailAddresses([]);
            return;
        }

        try {
            setEmailLoading(true);
            // 不传is_active参数，后端默认返回所有邮箱地址
            const response = await masterDataService.emailAddress.getEmailAddressesByGroup(targetGroup.id);
            setEmailAddresses(response.data || []);
        } catch (error) {
            console.error("加载邮箱地址失败:", error);
            message.error("加载邮箱地址失败");
        } finally {
            setEmailLoading(false);
        }
    };

    // 保存分组信息
    const handleSaveGroup = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            let response;
            if (mode === "create") {
                response = await masterDataService.emailGroup.createEmailGroup(values);
                message.success("创建成功");
                setNewGroup(response.data);
                setCurrentStep(1); // 切换到邮箱管理步骤
            } else {
                response = await masterDataService.emailGroup.updateEmailGroup({ id: group.id, data: values });
                message.success("更新成功");
            }

            onSuccess(response);
        } catch (error) {
            console.error("保存失败:", error);
            if (error.name !== "ValidationError") {
                message.error(error.response?.data?.detail || error.message || "操作失败");
            }
        } finally {
            setLoading(false);
        }
    };

    // ==================== 邮箱地址管理功能 ====================

    // 验证邮箱地址格式
    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // 解析批量邮箱输入
    const parseBatchEmails = (text) => {
        if (!text) return [];

        const emails = text
            .split(/[\n,;，；\s]+/)
            .map((email) => email.trim())
            .filter((email) => email.length > 0);

        return emails;
    };

    // 验证批量邮箱
    const validateBatchEmails = async () => {
        const emails = parseBatchEmails(batchEmails);
        const results = {
            valid: [],
            invalid: [],
            duplicates: [],
            existingInGroup: [], // 已在当前分组中的邮箱
        };

        if (emails.length === 0) {
            setValidationResults(results);
            return results;
        }

        // 先进行本地验证（格式和重复检查）
        const seenEmails = new Set();
        const validFormatEmails = [];

        emails.forEach((email) => {
            const trimmedEmail = email.toLowerCase();

            if (!validateEmail(email)) {
                results.invalid.push(email);
            } else if (seenEmails.has(trimmedEmail)) {
                results.duplicates.push(email);
            } else {
                validFormatEmails.push(email);
                seenEmails.add(trimmedEmail);
            }
        });

        // 检查哪些邮箱已经在当前分组中
        const currentGroupEmails = emailAddresses.map((item) => item.email.toLowerCase());

        validFormatEmails.forEach((email) => {
            const trimmedEmail = email.toLowerCase();
            if (currentGroupEmails.includes(trimmedEmail)) {
                results.existingInGroup.push(email);
            } else {
                // 所有格式正确且不在当前分组的邮箱都可以添加
                // 不管它们是否在系统的其他地方存在
                results.valid.push(email);
            }
        });

        setValidationResults(results);
        return results;
    };

    // 批量添加邮箱地址到分组
    const handleBatchAdd = async () => {
        const results = await validateBatchEmails();

        if (results.valid.length === 0) {
            message.warning("没有有效的邮箱地址可以添加");
            return;
        }

        const targetGroup = newGroup || group;
        if (!targetGroup?.id) {
            message.error("请先创建分组");
            return;
        }

        try {
            setBatchLoading(true);

            // 新的统一处理逻辑：使用后端的智能添加接口
            const response = await masterDataService.emailAddress.addEmailsToGroup({
                emails: results.valid,
                group_id: targetGroup.id,
            });

            if (response.data?.success) {
                const { created_count = 0, added_to_group_count = 0, failed_count = 0, failed_emails = [] } = response.data;

                const totalSuccess = created_count + added_to_group_count;

                if (totalSuccess > 0) {
                    let successMessage = `成功处理 ${totalSuccess} 个邮箱地址`;
                    if (created_count > 0 && added_to_group_count > 0) {
                        successMessage += ` (新建 ${created_count} 个，添加到分组 ${added_to_group_count} 个)`;
                    } else if (created_count > 0) {
                        successMessage += ` (新建 ${created_count} 个)`;
                    } else if (added_to_group_count > 0) {
                        successMessage += ` (添加到分组 ${added_to_group_count} 个)`;
                    }
                    message.success(successMessage);
                }

                if (failed_count > 0 && failed_emails.length > 0) {
                    const failedDetails = failed_emails.slice(0, 5).join(", ");
                    const moreInfo = failed_emails.length > 5 ? `等${failed_emails.length}个` : "";
                    message.warning(`${failed_count} 个邮箱地址处理失败: ${failedDetails}${moreInfo}`);
                }

                setBatchEmails("");
                setValidationResults(null);
                loadEmailAddresses();
            } else {
                message.error(response.data?.message || "批量添加失败");
            }
        } catch (error) {
            console.error("批量添加失败:", error);
            message.error(error.response?.data?.detail || error.message || "批量添加失败");
        } finally {
            setBatchLoading(false);
        }
    };

    // 从剪贴板粘贴
    const handlePasteFromClipboard = async () => {
        try {
            const text = await navigator.clipboard.readText();
            setBatchEmails((prev) => (prev ? `${prev}\n${text}` : text));
            message.success("已从剪贴板粘贴内容");
        } catch (error) {
            message.error("无法访问剪贴板，请手动粘贴");
        }
    };

    // 判断是否在编辑状态
    const isEditing = (record) => record.id === editingKey;

    // 开始编辑
    const edit = (record) => {
        emailForm.setFieldsValue({
            email: record.email,
            display_name: record.display_name,
            notes: record.notes,
            is_active: record.is_active,
        });
        setEditingKey(record.id);
    };

    // 取消编辑
    const cancel = () => {
        setEditingKey("");
        setAddingNew(false);
        emailForm.resetFields();
    };

    // 保存编辑
    const save = async (id) => {
        try {
            const values = await emailForm.validateFields();
            const targetGroup = newGroup || group;

            if (addingNew) {
                // 使用智能添加接口，避免重复邮箱的问题
                const response = await masterDataService.emailAddress.addEmailsToGroup({
                    emails: [values.email],
                    group_id: targetGroup.id,
                });

                // 智能添加接口返回的是包含统计信息的对象
                if (response.data?.success) {
                    const { created_count = 0, added_to_group_count = 0 } = response.data;
                    if (created_count > 0) {
                        message.success("新邮箱地址创建并添加到分组成功");
                    } else if (added_to_group_count > 0) {
                        message.success("邮箱地址添加到分组成功");
                    }
                    setAddingNew(false);
                    setEditingKey("");
                    loadEmailAddresses();
                } else {
                    message.error(response.data?.message || "添加失败");
                }
            } else {
                const response = await masterDataService.emailAddress.updateEmailAddress({ id, data: values });

                // 更新邮箱地址API返回的是资源对象，不是success/message格式
                if (response.data && response.data.id) {
                    message.success("更新成功");
                    setEditingKey("");
                    loadEmailAddresses();
                } else {
                    message.error("更新失败");
                }
            }

            emailForm.resetFields();
        } catch (error) {
            console.error("保存失败:", error);
            if (error.name === "ValidationError") {
                return;
            }
            message.error(error.response?.data?.detail || error.message || "保存失败");
        }
    };

    // 从分组中移除邮箱地址
    const handleRemoveFromGroup = async (addressId) => {
        const targetGroup = newGroup || group;
        if (!targetGroup?.id) {
            message.error("未找到当前分组");
            return;
        }

        try {
            const response = await masterDataService.emailAddress.batchUpdateAddressGroups({
                address_ids: [addressId],
                group_ids: [targetGroup.id],
                operation: "remove",
            });

            if (response.data?.success) {
                message.success(response.data.message || "从分组中移除成功");
                loadEmailAddresses();
            } else {
                message.error(response.data?.message || "移除失败");
            }
        } catch (error) {
            console.error("从分组中移除失败:", error);
            message.error(error.response?.data?.detail || error.message || "操作失败");
        }
    };

    // 添加新邮箱地址
    const handleAddNew = () => {
        const targetGroup = newGroup || group;
        if (!targetGroup?.id) {
            message.error("请先创建分组");
            return;
        }

        setAddingNew(true);
        setEditingKey("new");
        emailForm.setFieldsValue({
            is_active: true,
        });
    };

    // 邮箱地址表格列定义
    const emailColumns = [
        {
            title: "邮箱地址",
            dataIndex: "email",
            key: "email",
            width: 180,
            render: (text, record) => {
                if (isEditing(record) || (addingNew && record.id === "new")) {
                    return (
                        <Form.Item
                            name="email"
                            style={{ margin: 0 }}
                            rules={[
                                { required: true, message: "请输入邮箱地址" },
                                { type: "email", message: "请输入有效的邮箱地址" },
                                { max: 255, message: "邮箱地址不能超过255个字符" },
                            ]}
                        >
                            <Input placeholder="请输入邮箱地址" />
                        </Form.Item>
                    );
                }
                return (
                    <Space>
                        <MailOutlined />
                        {text}
                    </Space>
                );
            },
        },
        {
            title: "显示名称",
            dataIndex: "display_name",
            key: "display_name",
            width: 100,
            render: (text, record) => {
                if (isEditing(record) || (addingNew && record.id === "new")) {
                    return (
                        <Form.Item name="display_name" style={{ margin: 0 }} rules={[{ max: 100, message: "显示名称不能超过100个字符" }]}>
                            <Input placeholder="显示名称（可选）" />
                        </Form.Item>
                    );
                }
                return text || "无";
            },
        },
        {
            title: "备注",
            dataIndex: "notes",
            key: "notes",
            width: 140,
            ellipsis: true,
            render: (text, record) => {
                if (isEditing(record) || (addingNew && record.id === "new")) {
                    return (
                        <Form.Item name="notes" style={{ margin: 0 }} rules={[{ max: 500, message: "备注不能超过500个字符" }]}>
                            <TextArea placeholder="备注（可选）" rows={2} autoSize={{ minRows: 2, maxRows: 4 }} />
                        </Form.Item>
                    );
                }
                return text || "无";
            },
        },
        {
            title: "状态",
            dataIndex: "is_active",
            key: "is_active",
            width: 80,
            render: (is_active, record) => {
                if (isEditing(record) || (addingNew && record.id === "new")) {
                    return (
                        <Form.Item name="is_active" style={{ margin: 0 }} valuePropName="checked">
                            <Switch size="small" />
                        </Form.Item>
                    );
                }
                return <Tag color={is_active ? "green" : "red"}>{is_active ? "启用" : "停用"}</Tag>;
            },
        },
        {
            title: "操作",
            key: "action",
            width: 80,
            render: (_, record) => {
                if (isEditing(record) || (addingNew && record.id === "new")) {
                    return (
                        <Space>
                            <Button type="link" size="small" icon={<SaveOutlined />} onClick={() => save(record.id)}>
                                保存
                            </Button>
                            <Button type="link" size="small" icon={<CloseOutlined />} onClick={cancel}>
                                取消
                            </Button>
                        </Space>
                    );
                }

                if (addingNew && record.id !== "new") {
                    return null;
                }

                return (
                    <Space size="small">
                        <Tooltip title="编辑">
                            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => edit(record)} disabled={editingKey !== ""} />
                        </Tooltip>
                        <Popconfirm title="确定要从该分组中移除此邮箱地址吗？" onConfirm={() => handleRemoveFromGroup(record.id)} disabled={editingKey !== ""}>
                            <Tooltip title="从分组中移除">
                                <Button type="text" size="small" danger icon={<DeleteOutlined />} disabled={editingKey !== ""} />
                            </Tooltip>
                        </Popconfirm>
                    </Space>
                );
            },
        },
    ];

    // 准备表格数据
    const tableData = React.useMemo(() => {
        const addresses = emailAddresses || [];
        if (addingNew) {
            return [
                {
                    id: "new",
                    email: "",
                    display_name: "",
                    notes: "",
                    is_active: true,
                    created_at: null,
                },
                ...addresses,
            ];
        }
        return addresses;
    }, [emailAddresses, addingNew]);

    // 渲染验证结果
    const renderValidationResults = () => {
        if (!validationResults) return null;

        const { valid, invalid, duplicates, existingInGroup } = validationResults;

        return (
            <div style={{ marginTop: 16 }}>
                {valid.length > 0 && <Alert type="success" message={`${valid.length} 个有效邮箱地址可以添加到当前分组`} style={{ marginBottom: 8 }} />}
                {invalid.length > 0 && <Alert type="error" message={`${invalid.length} 个邮箱地址格式无效`} description={invalid.join(", ")} style={{ marginBottom: 8 }} />}
                {duplicates.length > 0 && <Alert type="warning" message={`${duplicates.length} 个重复的邮箱地址（已去重）`} description={duplicates.join(", ")} style={{ marginBottom: 8 }} />}
                {existingInGroup.length > 0 && <Alert type="info" message={`${existingInGroup.length} 个邮箱地址已在当前分组中（将跳过）`} description={existingInGroup.join(", ")} style={{ marginBottom: 8 }} />}
            </div>
        );
    };

    // 渲染创建模式的步骤
    const renderCreateSteps = () => (
        <div style={{ marginBottom: 24 }}>
            <Steps current={currentStep} size="small">
                <Step title="创建分组" description="设置分组基本信息" />
                <Step title="添加邮箱" description="批量添加邮箱地址" />
                <Step title="完成" description="配置完成" />
            </Steps>
        </div>
    );

    // 验证分组名称唯一性
    const validateGroupName = async (_, value) => {
        if (!value || value.trim() === "") {
            return Promise.resolve();
        }

        const trimmedValue = value.trim();

        // 检查是否包含@符号
        if (trimmedValue.includes("@")) {
            return Promise.reject(new Error('分组名称不能包含"@"符号，以避免与邮箱地址混淆'));
        }

        // 如果是编辑模式且名称没有变化，跳过唯一性检查
        if (mode === "edit" && group && trimmedValue === group.name) {
            return Promise.resolve();
        }

        // 检查名称唯一性
        try {
            setNameValidating(true);
            const response = await masterDataService.emailGroup.getEmailGroupList({
                search: trimmedValue,
                page: 1,
                page_size: 1,
            });

            // 检查是否有完全匹配的名称
            const exactMatch = response.data?.items?.find((item) => item.name.toLowerCase() === trimmedValue.toLowerCase() && (mode === "create" || item.id !== group?.id));

            if (exactMatch) {
                return Promise.reject(new Error(`分组名称"${trimmedValue}"已存在，请使用其他名称`));
            }

            return Promise.resolve();
        } catch (error) {
            console.error("验证分组名称失败:", error);
            // 如果验证接口出错，不阻止用户提交，让后端进行最终验证
            return Promise.resolve();
        } finally {
            setNameValidating(false);
        }
    };

    return (
        <Layout style={{ height: "100%" }}>
            {/* 分组信息编辑区域 */}
            <Card size="small">
                {mode === "create" && renderCreateSteps()}

                <Title level={5} style={{ margin: "0 0 16px 0" }}>
                    {mode === "create" ? "创建分组" : "分组信息"}
                </Title>

                <Form form={form} layout="horizontal" labelCol={{ span: 4 }} wrapperCol={{ span: 20 }}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="name"
                                label="分组名称"
                                rules={[
                                    { required: true, message: "请输入分组名称" },
                                    { max: 100, message: "分组名称不能超过100个字符" },
                                    { min: 2, message: "分组名称至少需要2个字符" },
                                    {
                                        pattern: /^[^@]*$/,
                                        message: '分组名称不能包含"@"符号',
                                    },
                                    { validator: validateGroupName },
                                ]}
                                validateFirst={true}
                                hasFeedback
                            >
                                <Input placeholder="请输入分组名称（不能包含@符号）" maxLength={100} showCount />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="is_active" label="状态" valuePropName="checked">
                                <Switch checkedChildren="启用" unCheckedChildren="停用" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item label="标签" name="tags">
                        <Select mode="tags" placeholder="请选择或输入标签" allowClear showSearch tokenSeparators={[","]} maxTagCount="responsive">
                            {availableTags.map((tag) => (
                                <Option key={tag} value={tag}>
                                    {tag}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item label="描述" name="description" rules={[{ max: 500, message: "描述不能超过500个字符" }]}>
                        <TextArea placeholder="请输入分组描述（可选）" showCount maxLength={500} autoSize={{ minRows: 2, maxRows: 4 }} />
                    </Form.Item>
                </Form>

                <Row justify="end" style={{ marginTop: 16 }}>
                    <Space>
                        <Button onClick={onCancel}>取消</Button>
                        <Button type="primary" loading={loading} onClick={handleSaveGroup} icon={<SaveOutlined />}>
                            {mode === "create" ? "创建" : "保存"}
                        </Button>
                    </Space>
                </Row>
            </Card>

            {/* 邮箱地址管理区域 */}
            {(mode !== "create" || currentStep >= 1) && (
                <Card style={{ marginTop: 16 }}>
                    <Tabs activeKey={activeTab} onChange={setActiveTab}>
                        <TabPane tab="邮箱列表" key="list">
                            <Row style={{ marginBottom: 16 }}>
                                <Col span={12}>
                                    <Space>
                                        <Button icon={<PlusOutlined />} onClick={handleAddNew} disabled={editingKey !== "" || addingNew}>
                                            添加邮箱地址
                                        </Button>
                                        <Button type="primary" ghost icon={<ImportOutlined />} onClick={() => setActiveTab("batch")}>
                                            批量导入
                                        </Button>
                                    </Space>
                                </Col>
                                <Col span={12} style={{ textAlign: "right" }}>
                                    <Text type="secondary" style={{ fontSize: 13 }}>
                                        当前分组共有 {emailAddresses.length} 个邮箱地址
                                    </Text>
                                </Col>
                            </Row>

                            <Form form={emailForm} component={false}>
                                <Table
                                    columns={emailColumns}
                                    dataSource={tableData}
                                    rowKey="id"
                                    loading={emailLoading}
                                    size="small"
                                    pagination={{
                                        pageSize: 30,
                                        showSizeChanger: true,
                                        showQuickJumper: true,
                                        showTotal: (total) => `共 ${total} 条记录`,
                                    }}
                                    rowSelection={{
                                        selectedRowKeys: selectedEmails,
                                        onChange: setSelectedEmails,
                                    }}
                                    scroll={{ y: 400 }}
                                />
                            </Form>

                            {selectedEmails.length > 0 && (
                                <div style={{ marginTop: 16, padding: 16, background: "#f5f5f5", borderRadius: 6 }}>
                                    <Space>
                                        <Text>已选择 {selectedEmails.length} 个邮箱地址</Text>
                                        <Button size="small" danger icon={<DeleteOutlined />}>
                                            批量删除
                                        </Button>
                                        <Button size="small" onClick={() => setSelectedEmails([])}>
                                            取消选择
                                        </Button>
                                    </Space>
                                </div>
                            )}
                        </TabPane>

                        <TabPane tab="批量导入" key="batch">
                            <Row gutter={16}>
                                <Col span={16}>
                                    <div style={{ marginBottom: 16 }}>
                                        <Space>
                                            <Button icon={<CopyOutlined />} onClick={handlePasteFromClipboard}>
                                                从剪贴板粘贴
                                            </Button>
                                            <Button
                                                onClick={() => {
                                                    setBatchEmails("");
                                                    setValidationResults(null);
                                                }}
                                            >
                                                清空
                                            </Button>
                                            <Button type="primary" onClick={() => validateBatchEmails()} disabled={!batchEmails.trim()}>
                                                验证邮箱
                                            </Button>
                                            <Button type="primary" loading={batchLoading} onClick={handleBatchAdd} disabled={!validationResults || validationResults.valid.length === 0}>
                                                添加 {validationResults?.valid.length || 0} 个邮箱地址
                                            </Button>
                                        </Space>
                                    </div>

                                    <TextArea
                                        value={batchEmails}
                                        onChange={(e) => setBatchEmails(e.target.value)}
                                        placeholder="请输入邮箱地址，支持多种分隔符：&#10;• 每行一个邮箱&#10;• 逗号分隔：email1@example.com, email2@example.com&#10;• 分号分隔：email1@example.com; email2@example.com&#10;• 空格分隔：email1@example.com email2@example.com"
                                        rows={10}
                                        showCount
                                        autoSize={{ minRows: 8, maxRows: 15 }}
                                    />

                                    {renderValidationResults()}
                                </Col>

                                <Col span={8}>
                                    <Card size="small" title="使用说明">
                                        <div>
                                            <p>
                                                <strong>支持的输入格式：</strong>
                                            </p>
                                            <ul>
                                                <li>每行一个邮箱地址</li>
                                                <li>逗号分隔（中英文）</li>
                                                <li>分号分隔（中英文）</li>
                                                <li>空格分隔</li>
                                            </ul>
                                        </div>

                                        <Alert type="info" message="💡 可以直接从Excel、记事本或其他应用复制邮箱列表，系统会自动识别格式" style={{ marginTop: 16, fontSize: 12 }} />
                                    </Card>
                                </Col>
                            </Row>
                        </TabPane>
                    </Tabs>
                </Card>
            )}
        </Layout>
    );
};

export default GroupManagementDrawer;
